import React from 'react';
import { FaBullseye, FaEye, FaHeart } from 'react-icons/fa';
import { motion } from 'framer-motion';

export default function SobreUsinaRosa() {
  return (
    <>
      {/* ===== Nossa História ===== */}
      <section className="relative w-full overflow-hidden py-24 px-6 bg-white">
        {/* Background Blobs Animados */}
        <motion.div
          className="absolute top-0 -left-64 w-96 h-96 bg-green-100 rounded-full filter blur-3xl opacity-40"
          animate={{ rotate: 360 }}
          transition={{ repeat: Infinity, duration: 20, ease: 'linear' }}
        />
        <motion.div
          className="absolute bottom-0 -right-64 w-[500px] h-[500px] bg-green-200 rounded-full filter blur-2xl opacity-30"
          animate={{ rotate: -360 }}
          transition={{ repeat: Infinity, duration: 25, ease: 'linear' }}
        />
        <motion.div
          className="absolute inset-0 bg-gradient-to-br from-white to-green-50"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
          style={{ mixBlendMode: 'multiply' }}
        />

        <div className="relative max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -60 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="space-y-6"
          >
            <h2 className="text-5xl font-extrabold text-green-800 tracking-tight">
              Nossa História
            </h2>
            <p className="text-gray-700 text-lg leading-relaxed">
              Fundada em <strong>1903</strong> pela família Rosa em Boituva-SP, a Usina Rosa S/A se consolidou como referência em destilação. Em <strong>2004</strong>, investimos em tecnologia de ponta e práticas sustentáveis para produzir etanol hidratado, anidro, neutro e cachaça com qualidade excepcional.
            </p>
            <p className="text-gray-700 text-lg leading-relaxed">
              Nossa localização na Rodovia Castelo Branco, Km 111, no bairro do Pinhal, otimiza nossa logística, garantindo entregas ágeis e seguras a postos de combustíveis e indústrias em todo o Brasil.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 60 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="w-full h-96 overflow-hidden rounded-3xl shadow-2xl"
          >
            <img
              src="/images/usina-historia.jpg"
              alt="História da Usina Rosa"
              className="w-full h-full object-cover"
            />
          </motion.div>
        </div>
      </section>

      {/* Divider */}
      <div className="w-full overflow-hidden leading-[0]">
        <svg className="relative block w-[calc(100%+1px)] h-16" viewBox="0 0 1440 54">
          <path fill="#ecfdf5" d="M0,0 C480,54 960,0 1440,54 L1440,54 L0,54 Z" />
        </svg>
      </div>

      {/* ===== Quem Somos ===== */}
      <section className="relative py-24 px-6 bg-gradient-to-br from-green-900 to-green-700 text-white overflow-hidden">
        {/* Animated Background Blobs */}
        <motion.div
          className="absolute top-1/4 left-[-10%] w-80 h-80 bg-white opacity-10 rounded-full filter blur-3xl animate-spin-slow"
        />
        <motion.div
          className="absolute bottom-1/4 right-[-15%] w-96 h-96 bg-white opacity-5 rounded-full filter blur-4xl animate-pulse"
        />

        <div className="relative max-w-5xl mx-auto text-center mb-16">
          <motion.h2
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-4xl md:text-5xl font-extrabold mb-4"
          >
            Quem Somos
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.5 }}
            className="text-lg leading-relaxed max-w-3xl mx-auto"
          >
            A Usina Rosa S/A combina tradição centenária e inovação tecnológica. Fundada em 1903 e liderada por gestão modernizada desde 2004, produzimos etanol e cachaça com excelência e responsabilidade ambiental.
          </motion.p>
        </div>

        <motion.div
          initial="hidden"
          animate="visible"
          variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.2 } } }}
          className="relative max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-10"
        >
          {[
            {
              icon: FaBullseye,
              title: 'Missão',
              desc: 'Garantir a qualidade de nossos produtos, aliando inovação e sustentabilidade em cada etapa da produção.'
            },
            {
              icon: FaEye,
              title: 'Visão',
              desc: 'Ser líder nacional em destilados, reconhecida pela excelência, tecnologia e compromisso socioambiental.'
            },
            {
              icon: FaHeart,
              title: 'Futuro',
              desc: 'Expandir nossa capacidade produtiva e fortalecer parcerias estratégicas, investindo em tecnologia limpa e inovação sustentável.'
            }
          ].map((feat, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + idx * 0.2, duration: 0.4 }}
              whileHover={{ scale: 1.1, transition: { duration: 0.15 } }}
              className="bg-white text-green-900 p-8 rounded-2xl shadow-xl flex flex-col items-center text-center transition-transform duration-150"
            >
              <div className="bg-gradient-to-br from-green-600 to-green-500 p-4 rounded-full mb-4 text-white">
                {React.createElement(feat.icon, { className: 'text-3xl' })}
              </div>
              <h3 className="text-2xl font-semibold mb-2 relative inline-block">
                {feat.title}
                <span className="absolute left-0 bottom-[-4px] w-full h-1 bg-green-500 rounded"></span>
              </h3>
              <p className="text-gray-700 leading-relaxed">
                {feat.desc}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </section>
    </>
  );
}
